import { getDoc } from 'firebase/firestore';
import { getGalleryDomainDoc } from '../lib/collections';

const DEFAULT_SUBDOMAIN = import.meta.env.VITE_DEFAULT_GALLERY_SUBDOMAIN || 'americanfineart';
const DEFAULT_GALLERY_ID = import.meta.env.VITE_DEFAULT_GALLERY_ID || 'AmericanFineArt_AZ';
const DEFAULT_GALLERY_NAME = import.meta.env.VITE_DEFAULT_GALLERY_NAME || 'American Fine Art';
const DEFAULT_VIEWER_BASE_DOMAIN = import.meta.env.VITE_VIEWER_BASE_DOMAIN || 'artvr.app';
const VIEWER_BASE_DOMAINS = (import.meta.env.VITE_VIEWER_BASE_DOMAINS || DEFAULT_VIEWER_BASE_DOMAIN)
  .split(',')
  .map((domain: string) => domain.trim().toLowerCase())
  .filter(Boolean);

const SUBDOMAIN_PATTERN = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;

export interface ResolvedGalleryDomain {
  subdomain: string;
  galleryId: string;
  galleryName: string;
  host: string;
  logoImagePath?: string;
  logoImageAlt?: string;
}

export function normalizeGallerySubdomain(value: string): string {
  return value.trim().toLowerCase();
}

export function isValidGallerySubdomain(value: string): boolean {
  return SUBDOMAIN_PATTERN.test(value);
}

function requestedSubdomainFromSearch(search: string): string | null {
  const params = new URLSearchParams(search);
  const requested = params.get('gallery') || params.get('subdomain');
  if (!requested) return null;

  const subdomain = normalizeGallerySubdomain(requested);
  return isValidGallerySubdomain(subdomain) ? subdomain : null;
}

function requestedSubdomainFromHost(hostname: string): string | null {
  const host = hostname.toLowerCase().split(':')[0];

  for (const baseDomain of VIEWER_BASE_DOMAINS) {
    if (host === baseDomain) {
      return DEFAULT_SUBDOMAIN;
    }

    if (host.endsWith(`.${baseDomain}`)) {
      const subdomain = host.slice(0, -(baseDomain.length + 1));
      return isValidGallerySubdomain(subdomain) ? subdomain : null;
    }
  }

  if (
    host === 'localhost' ||
    host === '127.0.0.1' ||
    host.endsWith('.pages.dev') ||
    host.endsWith('.localhost')
  ) {
    return DEFAULT_SUBDOMAIN;
  }

  return null;
}

export function getRequestedGallerySubdomain(
  hostname = window.location.hostname,
  search = window.location.search
): string {
  return requestedSubdomainFromSearch(search) || requestedSubdomainFromHost(hostname) || DEFAULT_SUBDOMAIN;
}

export async function resolveGalleryDomain(): Promise<ResolvedGalleryDomain> {
  const subdomain = getRequestedGallerySubdomain();
  const domainDoc = await getDoc(getGalleryDomainDoc(subdomain));

  if (domainDoc.exists()) {
    const data = domainDoc.data();

    if (data.active && data.galleryId) {
      return {
        subdomain,
        galleryId: data.galleryId,
        galleryName: data.galleryName || data.galleryId,
        host: data.host || `${subdomain}.${DEFAULT_VIEWER_BASE_DOMAIN}`,
        logoImagePath: data.logoImagePath || undefined,
        logoImageAlt: data.logoImageAlt || data.galleryName || data.galleryId
      };
    }
  }

  if (subdomain === DEFAULT_SUBDOMAIN) {
    return {
      subdomain: DEFAULT_SUBDOMAIN,
      galleryId: DEFAULT_GALLERY_ID,
      galleryName: DEFAULT_GALLERY_NAME,
      host: `${DEFAULT_SUBDOMAIN}.${DEFAULT_VIEWER_BASE_DOMAIN}`
    };
  }

  throw new Error(`No active gallery is configured for ${subdomain}.${DEFAULT_VIEWER_BASE_DOMAIN}.`);
}
